import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardBody, Container, Input, Spinner, Table, UncontrolledTooltip } from "reactstrap"
import dayjs from "dayjs"
import Breadcrumb from "components/Common/Breadcrumb"
import { formatRupees } from "components/AccountingPartnerReport"
import { getAccountingPartnerLeads } from "helpers/backendHelpers/accountingPartnerReport"

const PAGE_SIZE = 20
const BACK_TO_REPORT = "/setting?section=accountingPartners"

// The status the sales team set, as the partner sees it in the CA panel.
const LEAD_STATUS = {
  new: ["New", "info"],
  inProgress: ["In progress", "warning"],
  interested: ["Interested", "success"],
  notInterested: ["Not interested", "danger"],
  followUp: ["Follow up", "warning"],
  nurturing: ["Nurturing", "info"],
  demoCompleted: ["Demo completed", "info"],
  completed: ["Payment completed", "success"],
}

const StatusBadge = ({ status }) => {
  const [label, tone] = LEAD_STATUS[status] || [status || "-", "secondary"]
  return <span className={`badge badge-soft-${tone} font-size-12 px-2 py-1 text-${tone}`}>{label}</span>
}

const formatMobile = mobile => {
  const digits = String(mobile || "").replace(/\D/g, "").slice(-10)
  return digits.length === 10 ? `+91 ${digits.slice(0, 5)} ${digits.slice(5)}` : mobile || "-"
}

// Base amount after discount; a struck-through list price shows when a discount was given. Hover for the breakdown.
const PlanAmount = ({ id, plan }) => {
  const discounted = plan.discountAmount > 0
  const rows = [
    ["Plan amount", formatRupees(plan.listAmount)],
    ...(discounted ? [[plan.couponCode ? `Discount (${plan.couponCode})` : "Discount", `-${formatRupees(plan.discountAmount)}`]] : []),
    ["Base amount", formatRupees(plan.baseAmount)],
    ["GST", formatRupees(plan.taxAmount)],
    ["Total paid", formatRupees(plan.finalAmount)],
  ]
  const planLabel = [plan.name, plan.duration].filter(Boolean).join(" ")
  return (
    <>
      <div id={id} style={{ width: "fit-content" }}>
        {discounted && <span className="text-muted text-decoration-line-through me-2 font-size-12">{formatRupees(plan.listAmount)}</span>}
        <span>{formatRupees(plan.baseAmount)}</span>
        <div className="text-muted font-size-12">{planLabel}</div>
      </div>
      <UncontrolledTooltip target={id} placement="top" innerClassName="partner-tooltip">
        <div style={{ width: 280, textAlign: "left" }}>
          <div className="fw-bold mb-1">{planLabel}</div>
          {rows.map(([label, value], index) => (
            <div
              key={label}
              className="d-flex justify-content-between gap-3"
              style={index === rows.length - 1 ? { borderTop: "1px solid rgba(255,255,255,.3)", marginTop: 4, paddingTop: 4, fontWeight: 700 } : undefined}
            >
              <span>{label}</span>
              <span>{value}</span>
            </div>
          ))}
        </div>
      </UncontrolledTooltip>
    </>
  )
}

// One line in the table; hovering shows every note: the partner's own, then the sales team's.
const Notes = ({ id, partnerNote, salesNotes }) => {
  const notes = [...(partnerNote ? [`Partner: ${partnerNote}`] : []), ...salesNotes]
  if (!notes.length) return "-"
  return (
    <>
      <div id={id} className="text-truncate" style={{ maxWidth: 260 }}>{notes[notes.length - 1]}</div>
      <UncontrolledTooltip target={id} placement="top" innerClassName="partner-tooltip">
        <div style={{ maxWidth: 360, textAlign: "left", whiteSpace: "pre-wrap" }}>{notes.join("\n")}</div>
      </UncontrolledTooltip>
    </>
  )
}

const PartnerLeads = ({ match }) => {
  const { partnerId } = match.params
  const [data, setData] = useState(null)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(true)

  // Only the newest request may update the page.
  const latest = useRef(0)
  const load = useCallback(async () => {
    const current = ++latest.current
    setLoading(true)
    try {
      const result = await getAccountingPartnerLeads(partnerId, { page, limit: PAGE_SIZE, ...(search && { search }) })
      if (current === latest.current) setData(result.payload)
    } catch (_) {
      // api_helper already shows the error toast
    } finally {
      if (current === latest.current) setLoading(false)
    }
  }, [partnerId, page, search])

  useEffect(() => {
    const timer = setTimeout(load, search ? 400 : 0)
    return () => clearTimeout(timer)
  }, [load, search])

  const partner = data?.partner
  const leads = data?.leads || []
  const pages = Math.max(1, Math.ceil((data?.count || 0) / PAGE_SIZE))

  return (
    <div className="page-content">
      <Container fluid>
        {/* Bootstrap caps tooltips at 200px, too narrow for the plan breakdown. */}
        <style>{".partner-tooltip { max-width: none !important; }"}</style>
        <Breadcrumb title="Setting" breadcrumbItem="Accounting Partner" />
        <Link to={BACK_TO_REPORT} className="d-inline-block mb-3">
          <i className="bx bx-arrow-back me-1"></i>Accounting Partner Report
        </Link>

        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
          <div>
            <h4 className="font-size-20 mb-1">{partner ? partner.name : "My Leads"}</h4>
            <p className="text-muted mb-0">
              {partner
                ? [formatMobile(partner.mobileNo), partner.firmName].filter(Boolean).join(" · ")
                : "Leads this partner referred."}
              {" "}Status and sales notes come from the BillSaathi sales team.
            </p>
          </div>
          <Input
            style={{ maxWidth: 280 }}
            placeholder="Search mobile or contact name"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
          />
        </div>

        <Card>
          <CardBody>
            {loading && !data ? (
              <div className="text-center py-4"><Spinner color="primary" /></div>
            ) : (
              <div className="table-responsive" style={{ opacity: loading ? 0.6 : 1 }}>
                <Table className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Lead date time</th>
                      <th>Mobile number</th>
                      <th>Contact name</th>
                      <th>Business</th>
                      <th>Status</th>
                      <th>Notes</th>
                      <th>Plan base amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map(lead => (
                      <tr key={lead._id}>
                        <td className="text-nowrap">{dayjs(lead.createdAt).format("DD MMM YYYY, hh:mm A")}</td>
                        <td className="text-nowrap">{formatMobile(lead.mobileNo)}</td>
                        <td className="fw-medium">{lead.contactName || "-"}</td>
                        <td>{lead.businessType || "-"}</td>
                        <td><StatusBadge status={lead.leadStatus} /></td>
                        <td><Notes id={`notes-${lead._id}`} partnerNote={lead.partnerNote} salesNotes={lead.salesNotes} /></td>
                        <td className="text-nowrap">{lead.plan ? <PlanAmount id={`plan-${lead._id}`} plan={lead.plan} /> : "-"}</td>
                      </tr>
                    ))}
                    {!leads.length && (
                      <tr><td colSpan={7} className="text-center text-muted py-4">No leads yet.</td></tr>
                    )}
                  </tbody>
                  {leads.length > 0 && (
                    <tfoot>
                      <tr className="fw-bold table-light">
                        <td colSpan={6}>Total ({data.count} {data.count === 1 ? "lead" : "leads"})</td>
                        <td className="text-nowrap">{formatRupees(data.totalBaseAmount)}</td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            )}

            {pages > 1 && (
              <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                <button type="button" className="btn btn-light btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                <span className="text-muted font-size-13">Page {page} of {pages}</span>
                <button type="button" className="btn btn-light btn-sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  )
}

export default PartnerLeads
